import { Auth, signInWithCustomToken } from "firebase/auth";
import {
    browserSupportsWebAuthnAutofill,
    startAuthentication,
    startRegistration,
    WebAuthnAbortService,
} from "@simplewebauthn/browser";
import { Capacitor } from "@capacitor/core";
import { deviceConfig } from "./deviceconfig";
import { getLogger } from "./logger";

const logger = getLogger('passkeys');

/**
 * Passkey (WebAuthn) enrolment and sign-in.
 *
 * Firebase Auth has no native passkey support, so the ceremony runs against our own endpoints and
 * ends with the server minting a Firebase custom token for the uid the credential is bound to. From
 * Firebase's point of view this is an ordinary custom-token sign-in.
 *
 * Both ceremonies are two round-trips: fetch options (which include a server-issued, single-use
 * challenge), hand them to the authenticator, then post the signed result back for verification.
 */

export type PasskeySummary = {
    credentialId: string;
    label?: string;
    createdAt?: number;
    lastUsedAt?: number;
};

/** Raised for failures worth showing the user, as opposed to their cancelling the prompt. */
export class PasskeyError extends Error {}

/**
 * True when this device can create a passkey with a built-in authenticator (Touch ID, Face ID,
 * Windows Hello). Used to decide whether offering enrolment is worth the interruption -- there is no
 * point prompting on a device that would have nowhere to put the credential.
 *
 * Deliberately does not treat a security key as sufficient: the flow we are selling is "sign in with
 * your face", not "go find your YubiKey".
 */
export async function isPasskeySupported(): Promise<boolean> {
    // Never offer passkeys inside the native app. WKWebView exposes PublicKeyCredential and will
    // happily report a platform authenticator, but the ceremony cannot succeed there: the WebView's
    // origin is capacitor://localhost, and WebAuthn requires the relying-party ID to be a
    // registrable domain suffix of the caller's effective domain, so app.travelscroll.com is
    // rejected. Making the app go through the native ASAuthorization APIs is separate work; until
    // that lands, showing the option would only produce a button that always fails.
    if (Capacitor.isNativePlatform()) {
        return false;
    }
    if (typeof window === 'undefined' || !window.PublicKeyCredential) {
        return false;
    }
    try {
        return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (err) {
        logger.warn(`Could not determine platform authenticator availability: ${err}`);
        return false;
    }
}

/**
 * A human-readable name for the device a passkey is being created on, so the Settings list reads
 * "iPhone" or "Chrome on macOS" rather than a row of identical "Passkey" entries.
 *
 * Best-effort and cosmetic: it is a label, not an identifier, and two of the user's devices can
 * legitimately produce the same string. `isThisDevice` below is what actually distinguishes them.
 */
export function describeThisDevice(): string {
    if (Capacitor.isNativePlatform()) {
        const platform = Capacitor.getPlatform();
        return platform === 'ios' ? 'iPhone or iPad' : platform === 'android' ? 'Android device' : 'App';
    }
    if (typeof navigator === 'undefined') {
        return 'Passkey';
    }
    const agent = navigator.userAgent;
    const browser = /Edg\//.test(agent) ? 'Edge'
        : /Chrome\//.test(agent) ? 'Chrome'
            : /Firefox\//.test(agent) ? 'Firefox'
                : /Safari\//.test(agent) ? 'Safari'
                    : 'Browser';
    const os = /iPhone|iPad/.test(agent) ? 'iOS'
        : /Mac OS X/.test(agent) ? 'macOS'
            : /Android/.test(agent) ? 'Android'
                : /Windows/.test(agent) ? 'Windows'
                    : /Linux/.test(agent) ? 'Linux'
                        : undefined;
    return os ? `${browser} on ${os}` : browser;
}

/**
 * Credential IDs enrolled from this device.
 *
 * The server's list is account-wide and cannot tell which entries belong to the device asking --
 * a passkey lives in the platform keychain, and nothing in the WebAuthn response identifies the
 * machine. Recording the IDs locally is the only way Settings can say "this device", which matters
 * because revoking the passkey you are currently using has a very different consequence from
 * revoking one on a phone you have lost.
 */
const LOCAL_CREDENTIALS_KEY = 'passkeyCredentialsOnThisDevice';

function localCredentialIds(): string[] {
    try {
        const raw = window.localStorage?.getItem(LOCAL_CREDENTIALS_KEY);
        return raw ? JSON.parse(raw) as string[] : [];
    } catch {
        return [];
    }
}

function rememberLocalCredential(credentialId: string): void {
    try {
        const ids = localCredentialIds();
        if (!ids.includes(credentialId)) {
            window.localStorage?.setItem(LOCAL_CREDENTIALS_KEY, JSON.stringify([ ...ids, credentialId ]));
        }
    } catch {
        /* a missing label is cosmetic; never fail enrolment over it */
    }
}

function forgetLocalCredential(credentialId: string): void {
    try {
        window.localStorage?.setItem(
            LOCAL_CREDENTIALS_KEY,
            JSON.stringify(localCredentialIds().filter(id => id !== credentialId)));
    } catch {
        /* ignore */
    }
}

export function isThisDevice(credentialId: string): boolean {
    return localCredentialIds().includes(credentialId);
}

async function postJson(path: string, body: unknown, idToken?: string) {
    const response = await fetch(`${deviceConfig.apiBaseUrl}${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify(body ?? {}),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new PasskeyError(payload?.error ?? `Request to ${path} failed (${response.status})`);
    }
    return payload;
}

/**
 * The authenticator throws when the user dismisses the system sheet, which is a normal outcome
 * rather than a failure. Callers should fall silent for these instead of showing an error.
 */
export function isUserCancellation(err: unknown): boolean {
    const name = (err as { name?: string })?.name;
    return name === 'NotAllowedError' || name === 'AbortError';
}

/**
 * Enrols a passkey for the signed-in user.
 *
 * Requires a live Firebase session: the ID token is what binds the new credential to this uid rather
 * than creating a parallel account, which is why enrolment can only be offered after sign-in.
 */
export async function registerPasskey(auth: Auth, label?: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
        throw new PasskeyError('You must be signed in to add a passkey.');
    }
    const idToken = await user.getIdToken();

    const { challengeId, options } = await postJson('/v1/auth/passkeys/register/options', {}, idToken);
    const attestation = await startRegistration({ optionsJSON: options });
    const verified = await postJson(
        '/v1/auth/passkeys/register/verify',
        { challengeId, response: attestation, label: label ?? describeThisDevice() },
        idToken);

    if (verified?.credentialId) {
        rememberLocalCredential(verified.credentialId);
    }
    logger.log('Registered a new passkey');
}

/**
 * Signs in with a passkey.
 *
 * No username is collected: the options carry no allowCredentials, so the authenticator offers
 * whichever discoverable credential it holds for this site and the server resolves the account from
 * the credential ID it signs with.
 */
/**
 * Whether the browser can offer passkeys through its autofill menu ("conditional mediation").
 *
 * This is the only reliable way to answer "does this person have a passkey here?" without asking.
 * We cannot answer it ourselves: nobody is signed in yet, so there is no account to look up, and a
 * server that answered would leak which accounts exist. Local records are no substitute -- a
 * passkey can arrive from another device via iCloud Keychain without this browser ever writing
 * anything, and Safari's storage policies can evict what it did write. Conditional mediation moves
 * the decision into the browser, which knows the real answer and shows the option only when it has
 * one to show.
 *
 * False on the native app for the same reason isPasskeySupported() is -- see there.
 */
export async function isPasskeyAutofillSupported(): Promise<boolean> {
    if (Capacitor.isNativePlatform()) {
        return false;
    }
    try {
        return await browserSupportsWebAuthnAutofill();
    } catch (err) {
        logger.warn(`Could not determine autofill support: ${err}`);
        return false;
    }
}

/**
 * Stops a pending conditional-mediation request.
 *
 * A conditional call sits open until the user picks a passkey, so it has to be cancelled when the
 * screen goes away -- otherwise it stays armed and the next ceremony aborts the previous one at an
 * arbitrary moment.
 */
export function cancelPendingPasskeyRequest(): void {
    try {
        WebAuthnAbortService.cancelCeremony();
    } catch (err) {
        logger.log(`No passkey ceremony to cancel: ${err}`);
    }
}

/**
 * @param useBrowserAutofill run as conditional mediation, surfacing passkeys in the browser's
 *   autofill menu rather than opening a modal. Requires an <input> whose autocomplete attribute
 *   ends with "webauthn" to be present, and does not resolve until the user chooses one.
 */
export async function signInWithPasskey(
    auth: Auth,
    { useBrowserAutofill = false }: { useBrowserAutofill?: boolean } = {},
): Promise<void> {
    const { challengeId, options } = await postJson('/v1/auth/passkeys/login/options', {});
    const assertion = await startAuthentication({ optionsJSON: options, useBrowserAutofill });
    const { firebaseToken } = await postJson('/v1/auth/passkeys/login/verify', { challengeId, response: assertion });

    if (!firebaseToken) {
        throw new PasskeyError('Sign-in succeeded but no session was returned.');
    }
    await signInWithCustomToken(auth, firebaseToken);
    logger.log('Signed in with a passkey');
}

export async function listPasskeys(auth: Auth): Promise<PasskeySummary[]> {
    const user = auth.currentUser;
    if (!user) {
        return [];
    }
    const response = await fetch(`${deviceConfig.apiBaseUrl}/v1/auth/passkeys`, {
        headers: { Authorization: `Bearer ${await user.getIdToken()}` },
    });
    if (!response.ok) {
        throw new PasskeyError('Could not load your passkeys.');
    }
    return (await response.json()).passkeys ?? [];
}

export async function deletePasskey(auth: Auth, credentialId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
        throw new PasskeyError('You must be signed in to remove a passkey.');
    }
    const response = await fetch(
        `${deviceConfig.apiBaseUrl}/v1/auth/passkeys/${encodeURIComponent(credentialId)}`,
        {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${await user.getIdToken()}` },
        });
    if (!response.ok) {
        throw new PasskeyError('Could not remove that passkey.');
    }
    forgetLocalCredential(credentialId);
}
